import {Container, Typography} from '@mui/material';
import React, {useCallback, useEffect, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../../store/hooks';
import {
  getTeamSessionsAsync,
  setTeamSessionFilter,
} from '../../store/teamSessionSlice';
import PageTitle from '../shared/PageTitle';
import TeamSessionsList from '../shared/TeamSessionsList';
import TeamSessionFilter from '../shared/TeamSessionFilter';
import {TeamSession} from '../../interfaces';
import PageTitleContainer from '../shared/PageTitleContainer';
import FullScreenLoadingSpinner from '../shared/FullScreenLoading';

interface Props {}

const TeamSessions: React.FC<Props> = () => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const currentState = useAppSelector(state => ({
    teamSession: state.teamSession,
    team: state.team,
  }));
  const filter = useAppSelector(state => state.teamSession.teamSessionFilter);
  const teamSessionsLoading = useAppSelector(
    state => state.teamSession.status === 'loading',
  );
  const {teamId} = useParams();
  const teamIdInt = parseInt(teamId ?? '');

  useEffect(() => {
    dispatch(getTeamSessionsAsync(teamIdInt));
  }, [dispatch, teamIdInt]);

  const activeTeam = (currentState.team?.teams ?? []).filter(
    team => team.id === teamIdInt,
  )[0];

  const filterByPlayers = useCallback(
    (ts: TeamSession, requiredPlayerIds: string[]) => {
      // if no players are selected return any remaining sessions
      if (!requiredPlayerIds.length) {
        return true;
      }

      // return array of all player ids that are part of team session
      const sessionPlayerIds: number[] = (ts.playerSessions ?? [])
        .filter(ps => ps.player?.id)
        .map(ps => ps.player!.id!);
      // only return team sessions that include each selected player
      if (
        requiredPlayerIds.every(reqPlayerId =>
          sessionPlayerIds.includes(parseInt(reqPlayerId)),
        )
      ) {
        return true;
      }
      return false;
    },
    [],
  );

  const filteredTeamSessions: TeamSession[] = useMemo(() => {
    if (!filter) {
      return currentState.teamSession?.teamSessions ?? [];
    }
    const sessionBeginTime = filter.dateRange?.startDate?.valueOf() ?? null;
    const sessionEndDate = filter.dateRange?.endDate
      ? new Date(filter.dateRange.endDate)
      : null;
    const sessionEndTime = sessionEndDate
      ? sessionEndDate.setDate(sessionEndDate.getDate() + 1)
      : null;

    const {players} = filter;
    const requiredPlayerIds: string[] = (
      Object.keys(players) as unknown as Array<keyof Record<string, boolean>>
    ).filter(key => players[key]);

    return (currentState.teamSession?.teamSessions ?? []).filter(
      (ts: TeamSession) => {
        if (!ts.startTime) {
          return false;
        }
        const startTime = new Date(ts.startTime).valueOf();

        if (sessionBeginTime !== null && startTime < sessionBeginTime) {
          return false;
        }
        if (sessionEndTime !== null && startTime > sessionEndTime) {
          return false;
        }

        // filter by sessions
        const {sessionTypes, dataStatus, sessionMedia} = filter;

        if (dataStatus.pending && !dataStatus.processed) {
          // If no player sessions are still processing, return false
          if (!ts.hasPendingPlayerSessions) {
            return false;
          }
        }

        if (dataStatus.processed && !dataStatus.pending) {
          // If player sessions are still processing, return false
          if (ts.hasPendingPlayerSessions) {
            return false;
          }
        }

        if (sessionMedia?.hasVideo && !ts.hasVideo) {
          return false;
        }

        if (
          Object.values(sessionTypes).every(val => !val) ||
          Object.values(sessionTypes).every(val => val)
        ) {
          // any time the useMemo will return true, first filter by players
          return filterByPlayers(ts, requiredPlayerIds);
        }

        const validSessionTypes: string[] = (
          Object.keys(sessionTypes) as Array<keyof typeof sessionTypes>
        ).filter(key => sessionTypes[key]);
        if (!validSessionTypes.includes(ts.type as string)) {
          return false;
        }

        return filterByPlayers(ts, requiredPlayerIds);
      },
    );
  }, [currentState.teamSession?.teamSessions, filter, filterByPlayers]);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {pageName: t('pages.teamSessions')})}
        </title>
      </Helmet>
      <Container>
        <PageTitleContainer>
          <PageTitle noBottomMargin>{t('pages.teamSessions')}</PageTitle>
          <TeamSessionFilter
            onFilterUpdate={fil => dispatch(setTeamSessionFilter(fil))}
            currentFilters={filter}
            players={activeTeam?.players ?? []}
            teamId={activeTeam?.id}
          />
        </PageTitleContainer>
        {/* hide back button until we create a coach dashboard screen */}
        {/* <BackButton navigationUrl="/">
          {t('navigation.back', {pageName: t('pages.dashboard')})}
        </BackButton> */}
        {[...(filteredTeamSessions ?? [])].length > 0 ? (
          <TeamSessionsList
            teamSessions={[...(filteredTeamSessions ?? [])]}
            teamSize={
              (activeTeam?.players ?? []).filter(player => player.isActive)
                .length
            }
          />
        ) : (
          <>
            {teamSessionsLoading ? (
              <FullScreenLoadingSpinner />
            ) : (
              <Typography variant="h3" sx={{textAlign: 'center'}}>
                {t('sessionFilter.noSessions')}
              </Typography>
            )}
          </>
        )}
      </Container>
    </>
  );
};

export default TeamSessions;
